import { ReactElement, useState } from "react"

import Select from "react-select"
import { FormLabel, Input, Button } from "@wfp/ui"
import { SingleDatePicker } from "react-dates"
import moment, { Moment } from "moment/moment"

import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { Option } from "interfaces/dataInterfaces"
import CountrySelector from "../../Shared/HeaderFilters/CountrySelector"
import { renderMonthElement } from "../../../utils/dateHelper"

import { useDataModaUploadContext } from "../../../providers/DataModaUploadContext"

import { frequencyOptions } from "../../../constants/household"
import { useGetDCFQuery } from "../../../services/marketprices/dataCollectionFrequencies"

const ModaConnect = (): ReactElement => {
  const {
    keyValue,
    keyLabel,
    projects,
    setProjectId,
    forms,
    setSelectedFormId,
    handleTestFetching,
    testModaConnection,
    setCountry,
    modaForm,
    setModaForm,
    isLoading,
  } = useDataModaUploadContext()

  const [isFetchFocused, setIsFetchFocused] = useState<boolean>(false)
  const [isFocused, setFocused] = useState({
    startDate: false,
    endDate: false,
  })

  const { data: frequencies, isFetching: isLoadingFrequencies } = useGetDCFQuery({})
  const [frequency, setFrequency] = useState<number | null>(null)

  const handleCountrySelect = (country?: Option): void => {
    if (country) setCountry(country.value)
  }

  const handleIsEnabledTestButton = (): boolean => {
    const doNotCheck = ["formId", "mappingSchemaId"]
    const filteredModaForm: Record<string, unknown> = {}
    Object.keys(modaForm).forEach(key => {
      if (!doNotCheck.includes(key)) {
        filteredModaForm[key] = modaForm[key as keyof typeof modaForm]
      }
    })

    return Object.values(filteredModaForm).some(value => value === undefined || value === null)
  }

  const transformStringDateToMoment = (dateString: string | undefined): Moment | null => {
    if (!dateString) return null
    return moment(dateString, "MM-DD-YYYY")
  }

  return (
    <div className="row-auto">
      <div className="text-xl font-bold">Set up the connection to MODA</div>
      <p className="text-sm text-gray-500">Configure connection to MODA and verify access before mapping.</p>

      <div className="grid grid-cols-1 2xl:grid-cols-2 mt-2 gap-x-16 gap-y-4">
        <div>
          <FormLabel className="text-2m">Country</FormLabel>
          <CountrySelector onSelect={handleCountrySelect} className="w-100" />
        </div>

        <div>
          <FormLabel id="frequency" className="flex flex-wrap gap-2">
            Collection Frequency
            <div className="text-xs text-gray-500 italic">
              Select how often prices are collected. This determines data aggregation and processing.
            </div>
          </FormLabel>
          <Select
            inputId="frequency"
            placeholder="Select frequency"
            isLoading={isLoadingFrequencies}
            isDisabled={modaForm?.dataCollectionFrequencyId !== undefined}
            options={frequencies ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id.toString()}
            value={frequencies?.find(
              (f: any) => f.dataCollectionFrequencyID === Number(modaForm?.dataCollectionFrequencyId),
            )}
            onChange={selection => {
              setModaForm({
                ...modaForm,
                dataCollectionFrequencyId: selection?.id,
              })
            }}
            isClearable
            menuPosition="fixed"
            className="w-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-2 date-filters">
        <Input labelText="Start Date" invalidText="A valid value is required">
          {() => (
            <SingleDatePicker
              displayFormat="MM/DD/YYYY"
              showDefaultInputIcon
              renderMonthElement={renderMonthElement}
              inputIconPosition="after"
              onDateChange={newDate => {
                setModaForm({
                  ...modaForm,
                  dataCollectionStartDate: newDate ? newDate.format("MM-DD-YYYY") : undefined,
                })
              }}
              focused={isFocused?.startDate || false}
              onFocusChange={({ focused: isFocus }) => {
                setFocused({
                  startDate: isFocus,
                  endDate: false,
                })
              }}
              date={transformStringDateToMoment(modaForm.dataCollectionStartDate)}
              id="start_date"
              isOutsideRange={m => {
                if (!modaForm.endDate) return false
                const endDate = transformStringDateToMoment(modaForm.endDate)
                return !endDate ? false : m.unix() < endDate?.unix()
              }}
            />
          )}
        </Input>

        <Input labelText="End Date" invalidText="A valid value is required">
          {() => (
            <SingleDatePicker
              displayFormat="MM/DD/YYYY"
              showDefaultInputIcon
              renderMonthElement={renderMonthElement}
              inputIconPosition="after"
              onDateChange={newDate => {
                setModaForm({
                  ...modaForm,
                  dataCollectionEndDate: newDate ? newDate.format("MM-DD-YYYY") : undefined,
                })
              }}
              focused={isFocused?.endDate || false}
              onFocusChange={({ focused: isFocus }) => {
                setFocused({
                  startDate: false,
                  endDate: isFocus,
                })
              }}
              date={transformStringDateToMoment(modaForm.dataCollectionEndDate)}
              id="start_date"
              isOutsideRange={m => {
                if (!modaForm.endDate) return false
                const endDate = transformStringDateToMoment(modaForm.endDate)
                return !endDate ? false : m.unix() < endDate?.unix()
              }}
            />
          )}
        </Input>

        <Input labelText="Fetch Until" invalidText="A valid value is required">
          {() => (
            <SingleDatePicker
              displayFormat="MM/DD/YYYY"
              showDefaultInputIcon
              renderMonthElement={renderMonthElement}
              inputIconPosition="after"
              onDateChange={newDate => {
                setModaForm({
                  ...modaForm,
                  endDate: newDate ? newDate.format("MM-DD-YYYY") : undefined,
                })
              }}
              focused={isFetchFocused}
              onFocusChange={({ focused: isFocus }) => {
                setIsFetchFocused(isFocus)
              }}
              date={transformStringDateToMoment(modaForm.endDate)}
              id="start_date"
            />
          )}
        </Input>
      </div>

      <div className="grid grid-cols-1 mt-4">
        <FormLabel className="mb-1">Connection Details</FormLabel>
      </div>
      <div className="grid grid-cols-2 xl:gap-16 gap-4">
        <Select
          isSearchable
          isClearable
          isLoading={false}
          isDisabled
          placeholder={`${keyLabel}`}
          classNamePrefix="select"
          value={keyValue}
        />

        <Select
          isSearchable
          isClearable
          isLoading={false}
          placeholder="Projects"
          classNamePrefix="select"
          options={projects || []}
          onChange={selection => {
            setProjectId(selection ? Number(selection.value) : null)
            setModaForm({
              ...modaForm,
              projectId: selection ? Number(selection.value) : undefined,
            })
          }}
        />
      </div>
      <div className="grid grid-cols-2 mt-2 xl:gap-16 gap-4">
        <Select
          isSearchable
          isClearable
          isLoading={false}
          placeholder="Forms"
          classNamePrefix="select"
          options={forms || []}
          onChange={value => {
            setSelectedFormId(value ? Number(value.value) : null)
            setModaForm({
              ...modaForm,
              formId: value ? Number(value.value) : undefined,
            })
          }}
        />

        <Select
          isSearchable
          isClearable
          isLoading={false}
          placeholder="Fetch Frequency"
          classNamePrefix="select"
          options={frequencyOptions}
          value={frequencyOptions.find(f => f.value === frequency) ?? null}
          onChange={value => {
            setFrequency(value ? Number(value.value) : null)
            setModaForm({
              ...modaForm,
              frequencyId: value ? Number(value.value) : undefined,
            })
          }}
        />
      </div>

      <div className="flex mt-3 items-center justify-center">
        <Button
          kind="secondary"
          disabled={handleIsEnabledTestButton() || isLoading}
          className="min-w-[400px]"
          onClick={() => {
            handleTestFetching()
          }}
        >
          {isLoading ? <span>Testing...</span> : <span>Test Connection</span>}
        </Button>
      </div>

      {testModaConnection !== null && (
        <div className="mt-4">
          {testModaConnection === "false" && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-md shadow-sm">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faCircleXmark} size="lg" color="red" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800">Connection Failed</h4>
                <p className="text-sm text-red-700">Please check your API key or parameters and try again.</p>
              </div>
            </div>
          )}
          {testModaConnection === "true" && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border-l-4 border-green-500 rounded-md shadow-sm">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faCircleCheck} size="lg" color="green" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-green-800">Connection Successful!</h4>
                <p className="text-sm text-green-700">You can now proceed to the mapping step.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ModaConnect
